import { useQuery } from '@tanstack/react-query';

import { usePermissions } from '@/features/auth';
import { useSelectedCountry } from '@/features/country-scope';
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';

import type { SearchGroup, SearchHit } from '../types/global-search.types';
import { searchBrands, searchCustomers, searchOrders, searchProviders } from './global-search.api';

export const globalSearchKeys = {
  all: ['global-search'] as const,
  kind: (kind: string, term: string, countryId: number | undefined) =>
    [...globalSearchKeys.all, kind, term, countryId] as const,
};

const MIN_LENGTH = 2;

/**
 * Searches orders, customers, brands and providers — each only for staff with
 * that list's read permission, in the selected country. A number also offers
 * a direct jump to that order, complaint or customer.
 */
export function useGlobalSearch(input: string): SearchGroup[] {
  const { can } = usePermissions();
  const { country } = useSelectedCountry();
  const term = useDebouncedValue(input.trim(), 250);
  const ready = term.length >= MIN_LENGTH;
  const countryId = country?.id;

  const orders = useQuery({
    queryKey: globalSearchKeys.kind('orders', term, countryId),
    queryFn: ({ signal }) => searchOrders(term, countryId, signal),
    enabled: ready && can('orders.read'),
    staleTime: 30_000,
  });
  const customers = useQuery({
    queryKey: globalSearchKeys.kind('customers', term, countryId),
    queryFn: ({ signal }) => searchCustomers(term, countryId, signal),
    enabled: ready && can('customers.read'),
    staleTime: 30_000,
  });
  const brands = useQuery({
    queryKey: globalSearchKeys.kind('brands', term, countryId),
    queryFn: ({ signal }) => searchBrands(term, countryId, signal),
    enabled: ready && can('catalogue.read'),
    staleTime: 30_000,
  });
  const providers = useQuery({
    queryKey: globalSearchKeys.kind('providers', term, undefined),
    queryFn: ({ signal }) => searchProviders(term, signal),
    enabled: ready && can('providers.read'),
    staleTime: 30_000,
  });

  if (!ready) return [];

  const jumps: SearchHit[] = [];
  const number = /^#?(\d{1,12})$/.exec(term)?.[1];
  if (number) {
    const id = Number(number);
    if (can('orders.details.read'))
      jumps.push({
        kind: 'order',
        id,
        title: `Open order #${id}`,
        detail: null,
        to: `/orders/${id}`,
      });
    if (can('complaints.manage') || can('complaints.read'))
      jumps.push({
        kind: 'complaint',
        id,
        title: `Open complaint #${id}`,
        detail: null,
        to: `/complaints/${id}`,
      });
    if (can('customers.read'))
      jumps.push({
        kind: 'customer',
        id,
        title: `Open customer #${id}`,
        detail: null,
        to: `/customers/${id}`,
      });
  }

  const groups: SearchGroup[] = [
    { kind: 'order', heading: 'Go to', hits: jumps, isLoading: false },
    {
      kind: 'order',
      heading: 'Orders (last 30 days)',
      hits: orders.data ?? [],
      isLoading: orders.isFetching,
    },
    {
      kind: 'customer',
      heading: 'Customers',
      hits: customers.data ?? [],
      isLoading: customers.isFetching,
    },
    { kind: 'brand', heading: 'Brands', hits: brands.data ?? [], isLoading: brands.isFetching },
    {
      kind: 'provider',
      heading: 'Providers',
      hits: providers.data ?? [],
      isLoading: providers.isFetching,
    },
  ];
  return groups.filter((group) => group.hits.length > 0 || group.isLoading);
}
